import { DropiState } from "../../pages/SalesPage/SalesPageInterface";
import { PaidState } from "../../types/order/paidState";
import { StatusTone } from "./StatusDot";

// Plata sin cobrar es algo que espera a alguien, no un estado neutro: por eso
// "Por recaudar" va en ambar y no en gris. Vive aca y no en cada pantalla
// porque la tabla, el detalle y el filtro tienen que decir el mismo color.
export const getPaymentTone = (paymentState?: string | null): StatusTone =>
  paymentState === PaidState.PAID ? "success" : "warning";

// El color sigue el avance del envio y empata con la leyenda del grafico de
// Finance (SalesCycleChart STATUS_SEGMENTS), porque un mismo estado no puede
// tener dos colores en el producto:
//
//   neutral (gris claro) no arranco       slate  (#94a3b8) guia generada
//   violet               en gestion       info   (#3b82f6) en transito
//   success (#10b981)    entregado        dark   (#1f2937) cancelado
//   warning              espera a alguien error             roto
//
// Solo se alinean con Finance los estados que existen en ambos lados: guia
// generada, en transito, entregado y cancelado. `violet` se queda para la
// gestion interna (synced/confirmed) porque Finance no modela ese paso, y
// pasarlo a slate lo dejaba indistinguible de `neutral` en un punto de 6px —
// siendo que son dos estados consecutivos del flujo.
//
// `logistics_in_progress` sigue mapeado por compatibilidad: el backend ya no
// emite ese codigo (ahora manda `fulfilled`), pero durante la ventana de
// deploy el backend viejo todavia lo devuelve.
//
// Cancelado va en `dark` y no en gris: una orden recien creada esta viva y una
// cancelada esta muerta; el mismo gris borraba esa diferencia.
//
// Las claves son los `logistics_status_code` que emite el backend en
// OrderService.buildDropiLogisticsSummary. `dropi_observed` no esta aca a
// proposito: el backend title-casea cualquier estado que Dropi invente, asi que
// ese bucket no tiene limite y cae al default.
const LOGISTICS_TONES: Record<string, StatusTone> = {
  shipping_pending: "neutral",
  canceled: "dark",
  logistics_in_progress: "violet",
  dropi_synced: "violet",
  dropi_confirmed: "violet",
  dropi_guide_ready: "slate",
  dropi_delivered: "success",
  fulfilled: "success",
  dropi_waiting_payment: "warning",
  dropi_pending_sync: "warning",
  dropi_pending_confirmation: "warning",
  dropi_error: "error",
};

// El default es `violet` y no `info`: desde que el azul empata con Finance
// significa "en transito", y un estado desconocido de Dropi no da para
// afirmar eso.
export const getLogisticsTone = (statusCode?: string | null, dropiState?: DropiState): StatusTone => {
  if (dropiState === DropiState.ERROR) return "error";
  return LOGISTICS_TONES[statusCode || ""] || "violet";
};

// Estado de envio unificado (FulfillmentStatus del backend), alimentado por
// Shopify/Dropi/Mastershop o por cambio manual. Se mapea a los mismos tonos que
// el resto de la logistica para que un mismo estado no cambie de color segun de
// donde venga (p.ej. entregado siempre verde).
const FULFILLMENT_TONES: Record<string, StatusTone> = {
  PENDING: "neutral",
  GUIDE_GENERATED: "slate",
  PICKED_UP: "violet",
  IN_TRANSIT: "info",
  OUT_FOR_DELIVERY: "info",
  INCIDENT: "warning",
  DELIVERED: "success",
  RETURNED: "warning",
  CANCELED: "dark",
};

export const getFulfillmentTone = (status?: string | null): StatusTone =>
  FULFILLMENT_TONES[(status || "").trim().toUpperCase()] || "violet";

// Los `value` del filtro NO son los mismos codigos que manda el backend
// (el filtro dice `dropi_success`, el backend `dropi_synced`), asi que el mapeo
// va aparte. Si se reusa el de arriba, el mismo estado sale de un color en el
// filtro y de otro en la fila.
export const FILTER_TONES: Record<string, StatusTone> = {
  paid: "success",
  pending: "warning",
  unfulfilled: "neutral",
  canceled: "dark",
  fulfilled: "success",
  dropi_success: "violet",
  dropi_pending_sync: "warning",
  dropi_error: "error",
};
